// client/src/components/pages/Geofencing/liveMapVehicles.ts
//
// Live View's vehicle data for the map (LiveTrackingMap, ST-00244): a light motion status
// derived from speed + report age, the vehicle's colour key (ST-00253: the LexiStar status
// board's key when it has one, else its motion — map/assetStatus, via vehicleStatus
// liveStatusOf), and the GeoJSON the marker layers draw. The selected vehicle goes into its own,
// never-clustered collection, so it is never hidden inside a cluster bubble; every other
// vehicle goes into the clustered one.
//
// ST-00276 phase 2 (map colours & states): a vehicle's `display` (the organisation's display
// rules: its status board row's, else the rules over its motion) gives the colour key, hollow and
// the overlay marks the mark layers draw around the marker (map/assetDisplay).

import type { Feature, FeatureCollection, Point } from 'geojson';
import { assetMarkerFeatureProps, type AssetMarkerFeatureProps } from '../../map/assetMarkerImages';
import { assetMarkerKind } from '../../map/assetMarker';
import type { AssetStatusKey } from '../../map/assetStatus';
import { liveStatusOf } from './vehicleStatus';
import { NO_MARKS, displayMarkFeatureProps, type DisplayMarkFeatureProps } from '../../map/assetDisplay';
import { isHollowDisplay, type AssetDisplay } from '../../map/displayRules';
import type { FleetStatus, LiveVehiclePosition } from '../../../types/clientPages/geofencing';

export const MOVING_KMH = 3; // above this speed a vehicle reads as "moving"
export const STALE_MS = 5 * 60 * 1000; // no report for this long → offline / stale
/** The marker box on Live View (CSS px; includes the nose). */
export const LIVE_MARKER_PX = 40;

const reportAge = (v: Pick<LiveVehiclePosition, 'ts'>, now: number): number | null => {
  const t = v.ts ? new Date(v.ts).getTime() : NaN;
  return Number.isFinite(t) ? now - t : null;
};

/** No report for longer than STALE_MS. */
export function isStale(v: Pick<LiveVehiclePosition, 'ts'>, now = Date.now()): boolean {
  const age = reportAge(v, now);
  return age !== null && age > STALE_MS;
}

/**
 * The live (/live) feed has no status field (that lives on /fleet), so derive a light one from
 * speed + report age. Idling cannot be told from parked without ignition, so stationary but
 * recent reads as "parked".
 */
export function deriveStatus(
  v: Pick<LiveVehiclePosition, 'ts' | 'speed'>,
  now = Date.now()
): FleetStatus {
  if (isStale(v, now)) return 'offline';
  if ((v.speed || 0) > MOVING_KMH) return 'moving';
  return 'parked';
}

/** What the map keeps per vehicle between frames. */
export interface VehicleMeta {
  /** Motion (speed + report age). */
  status: FleetStatus;
  /** 'truck' | 'loader' (the live endpoint's missing class means loader). */
  vehicleClass: string;
  stale: boolean;
  /** The colour key: the LexiStar status board's, else the motion's. */
  key: AssetStatusKey;
  /** ST-00276: the display's marks (ring, pulse, badge) for the mark layers. */
  marks?: DisplayMarkFeatureProps;
  /** ST-00276: the display draws the marker hollow (a hollow token or a dashed mark). */
  hollow?: boolean;
}

export const vehicleMeta = (
  v: LiveVehiclePosition,
  now = Date.now(),
  lexistarKey?: AssetStatusKey | null,
  display?: AssetDisplay | null
): VehicleMeta => {
  const status = deriveStatus(v, now);
  return {
    status,
    vehicleClass: v.vehicleClass || 'loader',
    stale: isStale(v, now),
    key: liveStatusOf(status, lexistarKey, display).key,
    marks: display ? displayMarkFeatureProps(display.overlays) : NO_MARKS,
    hollow: display ? isHollowDisplay(display) : false,
  };
};

/** A rendered (interpolated) position. */
export interface RenderedPosition {
  lng: number;
  lat: number;
  heading: number;
}

export interface VehicleFeatureProps extends AssetMarkerFeatureProps, DisplayMarkFeatureProps {
  regNo: string;
  status: FleetStatus;
  statusKey: AssetStatusKey;
  vehicleClass: string;
  stale: 0 | 1;
  selected: 0 | 1;
}

export type VehicleCollection = FeatureCollection<Point, VehicleFeatureProps>;

export const EMPTY_VEHICLES: VehicleCollection = { type: 'FeatureCollection', features: [] };

/** One vehicle as a marker feature: key colour, kind, nose while moving, hollow when stale. */
export function vehicleFeature(
  regNo: string,
  pos: RenderedPosition,
  meta: Partial<VehicleMeta>,
  selected: boolean
): Feature<Point, VehicleFeatureProps> {
  const status = meta.status ?? 'offline';
  const vehicleClass = meta.vehicleClass || 'loader';
  const stale = !!meta.stale || status === 'offline' || !!meta.hollow;
  const statusKey = meta.key ?? liveStatusOf(status).key;
  return {
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [pos.lng, pos.lat] },
    properties: {
      regNo,
      status,
      statusKey,
      vehicleClass,
      stale: stale ? 1 : 0,
      selected: selected ? 1 : 0,
      ...(meta.marks ?? NO_MARKS),
      ...assetMarkerFeatureProps({
        kind: assetMarkerKind({ vehicleClass }),
        status: statusKey,
        heading: pos.heading,
        moving: status === 'moving',
        hollow: stale,
        size: LIVE_MARKER_PX,
      }),
    },
  };
}

/**
 * Split the rendered vehicles into the clustered collection and the selected one. The selected
 * vehicle is never in the clustered source, so a cluster bubble can never swallow it.
 */
export function buildVehicleCollections(
  rendered: ReadonlyMap<string, RenderedPosition>,
  meta: ReadonlyMap<string, VehicleMeta>,
  selectedRegNo: string | null
): { others: VehicleCollection; selected: VehicleCollection } {
  const others: Feature<Point, VehicleFeatureProps>[] = [];
  const selected: Feature<Point, VehicleFeatureProps>[] = [];
  rendered.forEach((pos, regNo) => {
    const isSel = regNo === selectedRegNo;
    (isSel ? selected : others).push(vehicleFeature(regNo, pos, meta.get(regNo) ?? {}, isSel));
  });
  return {
    others: { type: 'FeatureCollection', features: others },
    selected: { type: 'FeatureCollection', features: selected },
  };
}
